import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Phone } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { FadeIn, EASE } from "@/components/Reveal";
import { SERVICES, SERVICES_HEADER_IMAGE, CONTACT } from "@/data/site";

export default function Services() {
    return (
        <main data-testid="services-page">
            <PageHeader
                eyebrow="Our Services"
                title={
                    <>
                        Six systems.
                        <br />
                        One standard.
                    </>
                }
                lead="Professional lifting and access systems engineered for construction sites, high-rise buildings and industrial facilities."
                image={SERVICES_HEADER_IMAGE}
            />

            <section className="py-20 lg:py-28">
                <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-12">
                    <div className="grid gap-8 md:grid-cols-2 lg:gap-10">
                        {SERVICES.map((s, i) => (
                            <motion.article
                                key={s.id}
                                data-testid={`service-card-${s.id}`}
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.15 }}
                                transition={{ duration: 0.7, delay: (i % 2) * 0.1, ease: EASE }}
                                className="group relative flex flex-col overflow-hidden border border-line bg-card transition-all duration-500 hover:-translate-y-1.5 hover:border-accent/60 hover:shadow-[0_24px_60px_rgba(0,0,0,0.45)]"
                            >
                                <div className="relative aspect-[16/10] overflow-hidden">
                                    <img
                                        src={s.image}
                                        alt={`${s.name} system`}
                                        loading="lazy"
                                        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent" />
                                    <span className="absolute left-5 top-5 border border-white/20 bg-night/70 px-3 py-1.5 font-mono text-xs tracking-widest text-white backdrop-blur-sm">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span
                                        className="absolute bottom-0 left-0 h-0.5 w-0 bg-accent transition-all duration-500 group-hover:w-full"
                                        aria-hidden="true"
                                    />
                                </div>
                                <div className="flex flex-1 flex-col p-7 sm:p-8">
                                    <h2 className="font-display text-3xl font-bold uppercase leading-none text-white sm:text-4xl">
                                        {s.name}
                                    </h2>
                                    <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-400">
                                        {s.description}
                                    </p>
                                    <Link
                                        to={`/contact?service=${encodeURIComponent(s.name)}`}
                                        data-testid={`enquire-btn-${s.id}`}
                                        className="mt-7 inline-flex w-fit items-center gap-2 border border-line px-6 py-3 font-display text-sm font-bold uppercase tracking-widest text-white transition-all duration-300 hover:border-accent hover:bg-accent hover:text-night"
                                    >
                                        Enquire Now
                                        <ArrowUpRight className="h-4 w-4" />
                                    </Link>
                                </div>
                            </motion.article>
                        ))}
                    </div>
                </div>
            </section>

            <section className="border-t border-line bg-deck py-16 lg:py-20">
                <div className="mx-auto flex w-full max-w-7xl flex-col items-start justify-between gap-8 px-4 sm:px-6 lg:flex-row lg:items-center lg:px-12">
                    <FadeIn>
                        <h2 className="font-display text-3xl font-bold uppercase text-white sm:text-4xl">
                            Not sure which system fits your site?
                        </h2>
                        <p className="mt-2 text-sm text-slate-400">
                            Talk to our engineers — we will recommend the right lifting solution.
                        </p>
                    </FadeIn>
                    <FadeIn delay={0.1} className="flex flex-wrap gap-4">
                        <a
                            href={CONTACT.phones[0].href}
                            data-testid="services-call-btn"
                            className="flex items-center gap-2 border border-line px-6 py-3.5 font-display text-sm font-bold uppercase tracking-widest text-white transition-colors hover:border-accent hover:text-accent"
                        >
                            <Phone className="h-4 w-4" />
                            {CONTACT.phones[0].display}
                        </a>
                        <Link
                            to="/contact"
                            data-testid="services-enquire-btn"
                            className="flex items-center gap-2 bg-accent px-6 py-3.5 font-display text-sm font-bold uppercase tracking-widest text-night transition-colors hover:bg-accent-hover"
                        >
                            Enquire Now
                            <ArrowUpRight className="h-4 w-4" />
                        </Link>
                    </FadeIn>
                </div>
            </section>
        </main>
    );
}
